import React, { useState } from 'react'
import { isEmpty, isNil, startCase } from 'lodash'
import classNames from 'classnames'
import pluralize from 'pluralize'

import { Accordion } from '@/components/accordions/Accordion'
import { Button } from '@/components/buttons/Button'
import { Pill } from '@/components/pills/Pill'
import { ErrorMessage } from '@/components/forms/ErrorMessage'
import { UnifiedDiff } from '@/components/diff/UnifiedDiff'
import { formatContent, toListItems } from '@/utils/helpers'

import { normalizeFieldKey } from '../helpers'
import { typeModifier } from '../helpers/connectionRail.helpers'
import {
  hasPreviousValue,
  hasValueChanged,
  isRewrite,
  toComparableText,
} from '../helpers/noteComparison.helpers'

const TAXONOMY_FIELD_TYPES = ['pick_list', 'categorization']

// The mark beside a field's name. Long enough to name once rather than inline
// it twice in the className below.
const COLOR_BLOCK_CLASS = 'agent-summary-view__section-header__color-block'

// Both pick_list and categorization return the LLM's reasoning under an
// `explanation` key (or `explanation_` when the field itself is named
// "explanation"); pull that out rather than dumping the raw JSON.
const parseTaxonomyExplanation = (rawValue) => {
  if (isEmpty(rawValue)) {
    return null
  }
  let parsed
  try {
    parsed = JSON.parse(rawValue)
  } catch (error) {
    return null
  }
  if (isNil(parsed) || typeof parsed !== 'object') {
    return null
  }
  return parsed.explanation || parsed.explanation_ || null
}

// `fieldSkips` maps a field name to why the run never reached it — a gate that
// didn't pass, or an upstream field that was itself skipped. Membership is what
// marks a field as skipped, so those fields are pulled out of the result cards
// and summarised in one collapsed line underneath instead: a skip is flow
// control working, not a fault, and a red card per skipped field buries the
// fields that actually ran.
//
// `groupId` keeps that line's ids unique — the call channel renders this view
// once per visibility group.
//
// `previousValues` is what the call's saved note held for these same fields —
// the answer the live configuration produced. Where one exists, the field says
// whether the tested run moved it. A reworded answer shows the wording that
// changed; a rewritten one (see isRewrite) shows the new answer, with the
// original a click away, since a word diff of a rewrite is harder to read than
// either text alone.
//
// `fieldTypeKeys` maps a normalized field name to its display type, so the mark
// beside each name carries the same colour that field's type pill does in the
// editor.
export const AgentSummaryView = ({
  parsedSummary,
  fieldErrors = {},
  fieldResults = {},
  fieldSkips = {},
  previousValues = {},
  fieldTypeKeys = {},
  groupId = 'results',
}) => {
  const [skippedExpanded, setSkippedExpanded] = useState(false)
  // Rewritten fields whose saved answer the reader has opened.
  const [shownPrevious, setShownPrevious] = useState([])
  const fieldNames = Object.keys(parsedSummary)
  const skippedNames = fieldNames.filter((key) => !isNil(fieldSkips[key]))
  const executedNames = fieldNames.filter((key) => isNil(fieldSkips[key]))

  // A taxonomy field resolves the LLM's raw answer to one of its options; show
  // the resolved option as a badge (or flag when nothing matched) plus the
  // model's parsed explanation for the choice.
  const renderTaxonomyResult = (result, previous) => {
    const resolved = result.resolvedValue
    const noMatch = isNil(resolved) || resolved === ''
    const explanation = parseTaxonomyExplanation(result.rawValue)
    // An option name is a label, not prose — a word-level diff of "Billing" vs
    // "Payment arrangement" is noise, so the old option is shown as it was.
    const showPrevious = !isNil(previous) && hasValueChanged(previous, resolved)

    return (
      <div className="agent-summary-view__taxonomy">
        {noMatch ? (
          <span className="agent-summary-view__no-match">No matching option</span>
        ) : (
          <Pill small outlined>
            {String(resolved)}
          </Pill>
        )}
        {showPrevious && (
          <span className="agent-summary-view__previous-option">
            was <del>{String(previous)}</del>
          </span>
        )}
        {!isEmpty(explanation) && (
          <div className="agent-summary-view__explanation">{formatContent(explanation)}</div>
        )}
      </div>
    )
  }

  // A value as the reader sees it outside a diff: a list as a list, whichever
  // shape it came back in.
  const renderValue = (value) => {
    const listItems = toListItems(value)
    return formatContent(isNil(listItems) ? value : listItems)
  }

  // A rewritten answer reads as itself, with the saved one on request. A word
  // diff of a rewrite strikes out most of one answer and interleaves most of
  // the other, which is harder to read than either text alone.
  const renderRewrite = (key, previous, currentValue) => {
    const isShown = shownPrevious.includes(key)
    return (
      <div className="agent-summary-view__rewrite" data-testid={`field-rewrite-${key}`}>
        <div>{renderValue(currentValue)}</div>
        <Button
          text
          small
          className="agent-summary-view__rewrite-toggle"
          aria-expanded={isShown}
          onClick={() =>
            setShownPrevious(
              isShown ? shownPrevious.filter((name) => name !== key) : [...shownPrevious, key]
            )
          }
        >
          {isShown ? 'Hide original answer' : 'Show original answer'}
        </Button>
        {isShown && (
          <div
            className="agent-summary-view__saved-answer"
            data-testid={`field-saved-answer-${key}`}
          >
            {renderValue(previous)}
          </div>
        )}
      </div>
    )
  }

  return (
    <>
      <div className="agent-summary-view">
        {executedNames.map((key) => {
          const fieldError = fieldErrors[key]
          const hasError = !isEmpty(fieldError)
          const result = fieldResults[key]
          const isTaxonomy = TAXONOMY_FIELD_TYPES.includes(result?.fieldType)
          // What the saved note held for this field, when it held anything — a
          // field added since the note was written has nothing to compare to.
          // Presence rather than a non-nil value: the note can legitimately hold
          // null for a field that ran without resolving.
          const previous = previousValues[key]
          const hasPrevious = hasPreviousValue(previousValues, key)
          // A taxonomy field is compared on the option it resolved to, not on
          // parsedSummary's value — that falls back to the raw answer, which can
          // equal the saved option while this run actually resolved to nothing.
          const currentValue = isTaxonomy ? result?.resolvedValue : parsedSummary[key]
          const changed = hasPrevious && hasValueChanged(previous, currentValue)
          // Both sides through the same canonical text, so a list diffs as its
          // items rather than as "a,b" against "- a\n- b".
          const previousText = changed ? toComparableText(previous) : ''
          const currentText = changed ? toComparableText(currentValue) : ''
          const rewritten = changed && !isTaxonomy && isRewrite(previousText, currentText)
          // A field only the run knows about (nothing on screen to read a type
          // from) falls through to the default mark rather than an unstyled one.
          const typeKey = fieldTypeKeys[normalizeFieldKey(key)]

          return (
            <div
              className={classNames('agent-summary-container', { 'has-error': hasError })}
              key={key}
            >
              {/* h4: the drawer titles itself h2 and each visibility group h3,
                  so a field name sits a level below its group. */}
              <div className="agent-summary-headline">
                <div
                  className={classNames(COLOR_BLOCK_CLASS, {
                    [`${COLOR_BLOCK_CLASS}--${typeModifier(typeKey)}`]: !isNil(typeKey),
                  })}
                  data-testid={`field-type-mark-${key}`}
                />
                <h4 className="agent-summary-view__section-header">{startCase(key)}</h4>
                {/* "Changed" is the signal worth finding in a long note, so it
                    carries a badge; "Unchanged" says its piece quietly rather
                    than repeating a bordered pill down every row. */}
                {hasPrevious && !hasError && changed && (
                  <Pill small caution dataTestId={`comparison-marker-${key}`}>
                    Changed
                  </Pill>
                )}
                {hasPrevious && !hasError && !changed && (
                  <span
                    className="agent-summary-view__unchanged"
                    data-testid={`comparison-marker-${key}`}
                  >
                    Unchanged
                  </span>
                )}
              </div>
              {hasError && <ErrorMessage content={fieldError} />}
              {!hasError && isTaxonomy && renderTaxonomyResult(result, previous)}
              {/* Only a reworded prose field is worth a diff — an unchanged one
                  reads as itself, and marking nothing would just add chrome. */}
              {!hasError && !isTaxonomy && changed && !rewritten && (
                <div className="agent-summary-view__diff" data-testid={`field-diff-${key}`}>
                  <UnifiedDiff
                    plain
                    oldText={previousText}
                    newText={currentText}
                    emptyLabel="No wording changes."
                  />
                </div>
              )}
              {!hasError && rewritten && renderRewrite(key, previous, currentValue)}
              {!hasError && !isTaxonomy && !changed && <div>{renderValue(currentValue)}</div>}
            </div>
          )
        })}
      </div>

      {!isEmpty(skippedNames) && (
        <Accordion
          styled
          className="rtn-skipped-fields"
          id={`skipped-fields-${groupId}`}
          expanded={skippedExpanded}
          handleToggle={() => setSkippedExpanded((wasExpanded) => !wasExpanded)}
          title={`${skippedNames.length} ${pluralize('field', skippedNames.length)} ${
            skippedNames.length === 1 ? 'was' : 'were'
          } skipped`}
        >
          <ul className="rtn-skipped-fields__list">
            {skippedNames.map((key) => (
              <li key={key}>
                <span className="rtn-skipped-fields__name">{startCase(key)}</span>
                <span className="rtn-skipped-fields__reason">{fieldSkips[key]}</span>
              </li>
            ))}
          </ul>
        </Accordion>
      )}
    </>
  )
}
